import { Col, Row } from "react-bootstrap"
import { Form } from "react-bootstrap"
import withModal, { InjectedProps } from "../../components/HOC/WithModal"
import Pending from "../../components/Pending"
import React from "react"
import { GetApi } from "../../api/api"
import { FeatureCatalog, StreamVersions } from "../../api/types"
import { getFcDisplayName, isVersionLowerOrEqual, sortFcsByDate } from "../../util/featureCatalogUtils"
import { ConversionRuleProps } from "./ConversionRules"
import { Feedback, ObjectMapValidator, ObjectValidatorsMap, rangeCharactersValidator } from "../../components/Validator"
import { useHistory } from "react-router-dom"

const nameControlId = "name"
const fc1ControlId = "fc1Id"
const fc2ControlId = "fc2Id"

interface GetDataProps {
  baseFeatureCatalogs: FeatureCatalog[]
}

const getData = async (): Promise<GetDataProps> => {
  const api = await GetApi()
  const baseFeatureCatalogs = await api.getFeatureCatalogs(undefined, undefined, StreamVersions.SV_4110)
  return {
    baseFeatureCatalogs: baseFeatureCatalogs
      .filter((fc) => isVersionLowerOrEqual(fc.streamVersion || StreamVersions.SV_4110, StreamVersions.SV_4211))
      .sort(sortFcsByDate)
  }
}

interface FeatureCatalogsSelectFormProps extends GetDataProps, CreateConversionRuleModalExtProps {}

const FeatureCatalogsSelectForm = (props: FeatureCatalogsSelectFormProps) => {
  // tslint:disable:no-shadowed-variable
  const setName = (name: string) => props.updateConversionRule({ name })
  const setFc1Id = (fc1Id: string) => props.updateConversionRule({ fc1Id })
  const setFc2Id = (fc2Id: string) => props.updateConversionRule({ fc2Id })

  const { name, fc1Id, fc2Id } = props.conversionRule

  return (
    <Form>
      <ConversionRuleName
        nameValue={name || ""}
        setName={setName}
        feedback={props.validationFeedback}
        controlId={nameControlId}
      />
      <FeatureCatalogSelect
        text="Convert from feature catalog"
        baseFeatureCatalogs={props.baseFeatureCatalogs}
        fcId={fc1Id}
        setFcId={setFc1Id}
        disableFcId={fc2Id}
        controlId={fc1ControlId}
        feedback={props.validationFeedback}
      />
      <FeatureCatalogSelect
        text="Convert to feature catalog"
        baseFeatureCatalogs={props.baseFeatureCatalogs}
        fcId={fc2Id}
        setFcId={setFc2Id}
        disableFcId={fc1Id}
        controlId={fc2ControlId}
        feedback={props.validationFeedback}
      />
      <Form.Text>
        Note: Conversion rules can only be created with feature catalogs that are compatible with Quadri 2021 or newer.
      </Form.Text>
    </Form>
  )
}

interface FeatureCatalogSelectProps {
  text: string
  baseFeatureCatalogs: FeatureCatalog[]
  fcId?: string
  disableFcId?: string
  setFcId: (fcId: string) => void
  controlId?: string
  feedback: Feedback
}

const FeatureCatalogSelect = (props: FeatureCatalogSelectProps) => (
  <Form.Group as={Row} controlId={props.controlId}>
    <Form.Label column={true} md="3">
      {props.text} <span className="text-danger">*</span>
    </Form.Label>
    <Col md="9">
      <Form.Control
        as="select"
        className="custom-select"
        value={props.fcId || ""}
        // tslint:disable-next-line: jsx-no-lambda
        onChange={(e) => props.setFcId(e.target.value)}
        isInvalid={props.feedback.id === props.controlId}
      >
        <option value="">-- please select --</option>
        {props.baseFeatureCatalogs
          .filter((fc) => fc.id !== props.disableFcId)
          .map((fc) => (
            <option key={fc.id} value={fc.id} disabled={fc.id === props.disableFcId}>
              {getFcDisplayName(fc)}
            </option>
          ))}
      </Form.Control>
      <Form.Control.Feedback type="invalid">{props.feedback.message}</Form.Control.Feedback>
    </Col>
  </Form.Group>
)

interface ConversionRuleNameProps {
  nameValue: string
  setName: (name: string) => void
  controlId?: string
  feedback: Feedback
}

const ConversionRuleName = (props: ConversionRuleNameProps) => (
  <Form.Group as={Row} controlId={props.controlId}>
    <Form.Label column={true} md="3">
      Name <span className="text-danger">*</span>
    </Form.Label>
    <Col md="9">
      <Form.Control
        type="text"
        placeholder="My Conversion Rule"
        value={props.nameValue}
        // tslint:disable-next-line: jsx-no-lambda
        onChange={(e) => props.setName(e.target.value)}
        isInvalid={props.feedback.id === props.controlId}
      />
      <Form.Control.Feedback type="invalid">{props.feedback.message}</Form.Control.Feedback>
      <Form.Text>Name of the conversion rule</Form.Text>
    </Col>
  </Form.Group>
)

interface CreateConversionRuleModalExtProps extends InjectedProps {
  conversionRule: Partial<ConversionRuleProps>
  updateConversionRule: (rule: Partial<ConversionRuleProps>) => void
  validationFeedback: Feedback
}

const LoadCreateConversionRuleModal = (props: CreateConversionRuleModalExtProps) => {
  const renderSuccess = (dataProps: any) => <FeatureCatalogsSelectForm {...dataProps} {...props} />
  return <Pending getData={getData} renderSuccess={renderSuccess} />
}

const CreateConversionRuleModalInner = withModal(LoadCreateConversionRuleModal)

const CreateConversionRuleModal = (props: {
  showModal: boolean
  closeModal: () => void
  createConversionRule: (rule: ConversionRuleProps) => Promise<string>
}) => {
  const [conversionRule, setConversionRule] = React.useState({} as Partial<ConversionRuleProps>)
  const [validationFeedback, setValidationFeedback] = React.useState({ valid: true })
  const [creating, setCreating] = React.useState(false)

  const history = useHistory()

  const updateConversionRule = (rule: Partial<ConversionRuleProps>) => setConversionRule({ ...conversionRule, ...rule })

  const onSubmit = async () => {
    const feedback = NewConversionRuleValidator.validateObject(conversionRule)
    if (feedback.valid) {
      console.log("Submitting conversion rule: ", conversionRule)

      setValidationFeedback(feedback)
      setCreating(true)

      const conversionRuleId = await props.createConversionRule(conversionRule as ConversionRuleProps)

      setCreating(false)
      props.closeModal()

      history.push(`/conversionrules/${conversionRuleId}`)
    } else {
      console.log("Setting validation feedback: ", feedback)
      setValidationFeedback(feedback)
    }
  }

  return (
    <CreateConversionRuleModalInner
      isOpen={props.showModal}
      onCancel={props.closeModal}
      toggle={onSubmit} // When cancel is defined, toggle is misnamed. It should be onSubmit...
      headerText="New Conversion Rule"
      toggleText={creating ? "Creating..." : "Create"}
      size="xl"
      conversionRule={conversionRule}
      updateConversionRule={updateConversionRule}
      validationFeedback={validationFeedback}
    />
  )
}

const NewConversionRuleValidatorMap: ObjectValidatorsMap<{ name?: string; fc1Id?: string; fc2Id?: string }> = {
  name: rangeCharactersValidator(
    1,
    101,
    "Conversion rule name is required with a length between 2 and 100 characters."
  ),
  fc1Id: rangeCharactersValidator(1, 101, "Two feature catalogs are required"),
  fc2Id: rangeCharactersValidator(1, 101, "Two feature caralogs are required")
}

const NewConversionRuleValidator = new ObjectMapValidator<{ name?: string; fc1Id?: string; fc2Id?: string }>(
  NewConversionRuleValidatorMap
)

export default CreateConversionRuleModal
